import { useRef, useState } from "react";
import { FileText, Download, Upload, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type {
  DealerPortalVehicleDocument,
  DealerPortalVehicleCarTestSummary,
} from "@/types/dealerPortal";
import { useLanguage } from "@/context/LanguageContext";
import { dealerPortalLabels, vehicleCardUILabels } from "@/i18n";
import { VehicleTestDocumentView } from "@/admin/pages/VehicleTestDocumentView";
import { VehicleTest } from "@/types/vehicleTest";
import { TestService } from "@/services/test/testService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";

export const DOCUMENT_CATEGORIES = {
  OWNER_CHANGE: "OWNER_CHANGE",
  VEHICLE_TEST: "VEHICLE_TEST",
  OTHER: "OTHER",
} as const;
export type DealerPortalDocumentCategory =
  (typeof DOCUMENT_CATEGORIES)[keyof typeof DOCUMENT_CATEGORIES];

function getDocumentsByCategory(
  documents: DealerPortalVehicleDocument[],
  category: DealerPortalDocumentCategory,
): DealerPortalVehicleDocument[] {
  return documents.filter((doc) => {
    if (doc.imageCategory) return false;
    const docCat = doc.documentCategory ?? "OTHER";
    return docCat === category;
  });
}

const TAB_IDS = ["OWNER_CHANGE", "VEHICLE_TEST", "OTHER"] as const;

interface DealerPortalVehicleDocumentsProps {
  vehicleId: string;
  documents: DealerPortalVehicleDocument[];
  carTests?: DealerPortalVehicleCarTestSummary[];
  carTestsLoading?: boolean;
  onUpload: (
    file: File,
    documentCategory: DealerPortalDocumentCategory,
  ) => void;
  onDownload: (doc: DealerPortalVehicleDocument) => void;
  onDelete: (doc: DealerPortalVehicleDocument) => void;
  isUploading: boolean;
  deletingDocId: string | null;
}

export function DealerPortalVehicleDocuments({
  vehicleId,
  documents,
  carTests = [],
  carTestsLoading = false,
  onUpload,
  onDownload,
  onDelete,
  isUploading,
  deletingDocId,
}: DealerPortalVehicleDocumentsProps) {
  const { language } = useLanguage();
  const d = dealerPortalLabels(language);
  const v = d.vehicles as Record<string, string>;
  const vc = vehicleCardUILabels(language);
  const openDocumentPickerRef = useRef<(() => void) | null>(null);
  const pendingCategoryRef = useRef<DealerPortalDocumentCategory>("OTHER");
  const [viewTest, setViewTest] = useState<VehicleTest | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  const tabLabels: Record<(typeof TAB_IDS)[number], string> = {
    OWNER_CHANGE: v.docTabOwnership ?? vc.ownership ?? "Ägarbyte",
    VEHICLE_TEST: v.docTabVehicleTest ?? vc.test ?? "Fordonstest",
    OTHER: v.docTabOther ?? vc.otherDocuments ?? "Övriga dokument",
  };
  const emptyMessages: Record<(typeof TAB_IDS)[number], string> = {
    OWNER_CHANGE: v.docNoOwnerChange ?? "Inga ägarbytedokument än.",
    VEHICLE_TEST:
      v.docNoVehicleTest ??
      vc.vehicleTestsFromAnnons ??
      "Fordonstester skapas när ni lägger upp en annons.",
    OTHER: v.docNoOther ?? "Inga övriga dokument än.",
  };

  const handleUploadClick = (category: DealerPortalDocumentCategory) => {
    if (category === "VEHICLE_TEST") return; // No upload for vehicle tests
    pendingCategoryRef.current = category;
    openDocumentPickerRef.current?.();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const category = pendingCategoryRef.current;
    onUpload(file, category);
  };

  const handleTestClick = async (testId: string) => {
    setTestLoading(true);
    setViewTest(null);
    try {
      const test = await TestService.getTestById(testId);
      if (test) setViewTest(test);
    } finally {
      setTestLoading(false);
    }
  };

  const getDocMedia = (doc: DealerPortalVehicleDocument) => {
    const m = doc.media;
    return Array.isArray(m) ? m[0] : m ?? null;
  };

  return (
    <div className="space-y-6">
      <Dialog open={!!viewTest || testLoading} onOpenChange={(open) => !open && setViewTest(null)}>
        <DialogContent
          showCloseButton={true}
          className="max-w-3xl w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        >
          <DialogHeader>
            <DialogTitle>
              {viewTest
                ? [
                    viewTest.vehicleDetails?.make,
                    viewTest.vehicleDetails?.model,
                    viewTest.vehicleDetails?.year,
                  ]
                    .filter(Boolean)
                    .join(" ") || (vc.test ?? "Fordonstest")
                : vc.test ?? "Fordonstest"}
            </DialogTitle>
          </DialogHeader>
          {testLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {viewTest && !testLoading && <VehicleTestDocumentView data={viewTest} />}
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="OWNER_CHANGE" className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto overflow-y-hidden flex-nowrap scrollbar-hide h-9 sm:h-10 text-xs sm:text-sm p-1">
          {TAB_IDS.map((tabId) => (
            <TabsTrigger
              key={tabId}
              value={tabId}
              className="flex-none min-w-0 sm:flex-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5"
            >
              {tabLabels[tabId]}
            </TabsTrigger>
          ))}
        </TabsList>

        {TAB_IDS.map((tabId) => {
          if (tabId === "VEHICLE_TEST") {
            const testsByStatus = {
              own: carTests.filter((t) => t.status === "ONGOING"),
              pending: carTests.filter((t) => t.status === "PENDING_REVIEW"),
              approved: carTests.filter((t) => t.status === "APPROVED"),
              rejected: carTests.filter((t) => t.status === "REJECTED"),
            };
            const formatDate = (dateStr: string | null) =>
              dateStr
                ? new Date(dateStr).toLocaleDateString(
                    language === "en" ? "en-GB" : "sv-SE",
                    { day: "numeric", month: "short", year: "numeric" },
                  )
                : "—";
            const renderTestList = (
              list: DealerPortalVehicleCarTestSummary[],
              emptyKey: keyof typeof vc,
            ) => (
              <div className="space-y-3">
                {carTestsLoading ? (
                  <div className="flex items-center justify-center py-12 text-muted-foreground">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : list.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    {vc[emptyKey] ?? emptyMessages.VEHICLE_TEST}
                  </p>
                ) : (
                  list.map((t) => (
                    <Card
                      key={t.id}
                      className="shadow-sm cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleTestClick(t.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <h4 className="font-medium truncate">
                              {[
                                t.carTestInformation?.make,
                                t.carTestInformation?.model,
                                t.carTestInformation?.year != null
                                  ? String(t.carTestInformation.year)
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(" ") ||
                                t.registrationNo ||
                                t.id}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(t.submittedAt ?? t.createdAt)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            );
            return (
              <TabsContent key={tabId} value={tabId} className="space-y-6 mt-4">
                <Tabs defaultValue="own" className="w-full">
                  <TabsList className="w-full justify-start overflow-x-auto overflow-y-hidden flex-nowrap scrollbar-hide h-9 sm:h-10 text-xs sm:text-sm p-1">
                    <TabsTrigger
                      value="own"
                      className="flex-none min-w-0 sm:flex-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5"
                    >
                      {vc.ownTests ?? "Egna tester"}
                    </TabsTrigger>
                    <TabsTrigger
                      value="pending"
                      className="flex-none min-w-0 sm:flex-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5"
                    >
                      {vc.awaitingReview}
                    </TabsTrigger>
                    <TabsTrigger
                      value="approved"
                      className="flex-none min-w-0 sm:flex-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5"
                    >
                      {vc.approved}
                    </TabsTrigger>
                    <TabsTrigger
                      value="rejected"
                      className="flex-none min-w-0 sm:flex-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5"
                    >
                      {vc.rejected}
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="own" className="space-y-6 mt-4">
                    {renderTestList(testsByStatus.own, "noOwnTests")}
                  </TabsContent>
                  <TabsContent value="pending" className="space-y-6 mt-4">
                    {renderTestList(testsByStatus.pending, "noUnhandledTests")}
                  </TabsContent>
                  <TabsContent value="approved" className="space-y-6 mt-4">
                    {renderTestList(testsByStatus.approved, "noApprovedTests")}
                  </TabsContent>
                  <TabsContent value="rejected" className="space-y-6 mt-4">
                    {renderTestList(testsByStatus.rejected, "noRejectedTests")}
                  </TabsContent>
                </Tabs>
              </TabsContent>
            );
          }

          const list = getDocumentsByCategory(
            documents,
            tabId as DealerPortalDocumentCategory,
          );
          return (
            <TabsContent key={tabId} value={tabId} className="space-y-6 mt-4">
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={() =>
                    handleUploadClick(tabId as DealerPortalDocumentCategory)
                  }
                  disabled={isUploading}
                  className="gap-2"
                >
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {(
                    v.docUploadCategory ??
                    vc.uploadCategory ??
                    "Ladda upp {category}"
                  ).replace("{category}", tabLabels[tabId].toLowerCase())}
                </Button>
              </div>

              <FileUploadTrigger
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                onFilesSelected={(files) => {
                  if (!files[0]) return;
                  const dt = new DataTransfer();
                  dt.items.add(files[0]);
                  handleFileChange({
                    target: { files: dt.files },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
              >
                {(open) => {
                  openDocumentPickerRef.current = open;
                  return null;
                }}
              </FileUploadTrigger>

              <div className="space-y-3">
                {list.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    {emptyMessages[tabId]}
                  </p>
                ) : (
                  list.map((doc) => (
                    <Card key={doc.id} className="shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                              <h4 className="font-medium truncate">
                                {getDocMedia(doc)?.originalName ?? doc.id}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {doc.createdAt
                                  ? new Date(doc.createdAt).toLocaleDateString(
                                      language === "en" ? "en-GB" : "sv-SE",
                                    )
                                  : ""}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDownload(doc)}
                              disabled={!getDocMedia(doc)?.id}
                              title={
                                v.download ?? vc.downloadDocument ?? "Ladda ned"
                              }
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDelete(doc)}
                              disabled={deletingDocId === doc.id}
                              title={v.delete ?? "Radera"}
                            >
                              {deletingDocId === doc.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4 text-destructive" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
